/**
 * @fileoverview 메시지를 전송합니다.
 * 메시지를 전송하면 반환값을 optimistic update 합니다. 실제로 메시지가 서버에서 전송 처리 되어있지 않을수도 있지만, 유저에게 빠른 피드백을 제공할 수 있습니다.
 * 이 함수를 호출한 뒤, 서버와 동기화 하는 절차는 아래와 같습니다.
 * 1. 로컬 데이터에 이 함수를 통해 전송한 메시지 저장
 * 2. getMessages 함수를 호출해 반환된 마지막 메시지 id가 이 함수를 통해 로컬 데이터에 저장한 메시지 id와 같은지 검증
 * 3. 같지 않다면 exponential backoff 로직을 통해 최대 3회 getMessages 함수를 호출
 */
import { SendMessageRequest } from "@daangn/idl/rocketchat/api/api";
import type {
  Image as RocketchatImage,
  User_Type,
} from "@daangn/idl/rocketchat/api/model";
import type { Values } from "@karrotmarket-com/type-utility";
import { isNil } from "lodash";
import { sendRocketchatRequestWithPromise } from "~/rocketchat/rocketChatClient";
import type { ChatMessageType } from "../chatMessage.entity";
import {
  getNormalizedChatMessage,
  getNormalizedChatMessageById,
} from "../chatMessageNormalizer";
import { getMessagePrimaryKey } from "../getMessagePrimaryKey";
import { getMessages } from "./getMessages";

export async function sendMessage(
  {
    message,
  }: {
    message: {
      channelId: string;
      type: Values<typeof ChatMessageType>;
      receiver: {
        id: string;
        type: User_Type;
      };
      sender: {
        id: string;
        type: User_Type;
      };
      content: string;
      images?: RocketchatImage[];
    };
  },
  socket: WebSocket,
) {
  const response = await sendRocketchatRequestWithPromise({
    chatRequest: {
      name: "SendMessageRequest",
      send_message: SendMessageRequest.create({
        message: {
          channel_id: message.channelId,
          content: message.content,
          images: message.images ?? [],
          receiver: {
            id: message.receiver.id,
            type: message.receiver.type,
          },
          sender: {
            id: message.sender.id,
            type: message.sender.type,
          },
          type: message.type,
        },
      }),
    },
    socket,
  });

  const sendMessageResponse = response?.send_message;

  if (isNil(sendMessageResponse)) {
    throw new Error("handleSendMessageResponse: response is nil");
  }

  const channelId = sendMessageResponse.message?.channel_id;

  if (isNil(channelId)) {
    throw new Error("handleSendMessageResponse: channelId is nil");
  }

  const normalizedChatMessage = getNormalizedChatMessage({
    message: sendMessageResponse.message ?? {},
  });
  const chatMessageId =
    normalizedChatMessage.result ??
    getMessagePrimaryKey({
      message: {
        channelId,
        id: sendMessageResponse.message?.id ?? "",
      },
    });
  const normalizedChatMessageById = getNormalizedChatMessageById({
    chatMessageId,
    normalizedData: normalizedChatMessage,
  });

  getMessages(
    {
      channelId,
      limit: 2,
    },
    socket,
  ).catch(console.error);

  return {
    message: normalizedChatMessageById,
    raw: sendMessageResponse,
  };
}
